import {
  useEffect,
  useMemo,
  useState
} from 'react';
import { createPortal } from 'react-dom';

// UNIFIED_CMS_DATE_PICKER_V1

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function contextHeaders() {
  return {
    'apikey':
      SUPABASE_KEY,

    'Authorization':
      'Bearer ' +
      SUPABASE_KEY
  };
}


async function contextRows(
  url,
  label
) {
  const response =
    await fetch(
      url,
      {
        headers:
          contextHeaders()
      }
    );

  const body =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
      ' failed (' +
      response.status +
      '): ' +
      (
        body ||
        response.statusText
      )
    );
  }

  if (!body) {
    return [];
  }

  const parsed =
    JSON.parse(body);

  return Array.isArray(parsed)
    ? parsed
    : [];
}


export function emptyCourseDatePickerContext() {
  return {
    weeks: [],
    assignments: [],
    typeSettings: {},
    scheduleItems: [],
    scheduleReadings: [],
    scheduleEvents: [],
    holidays: []
  };
}


async function loadDatePickerHolidays(
  instructorId
) {
  if (!instructorId) {
    return [];
  }

  const instructors =
    await contextRows(
      SUPABASE_URL +
        '/rest/v1/instructors?id=eq.' +
        encodeURIComponent(
          instructorId
        ) +
        '&select=institution_id&limit=1',

      'Loading instructor institution'
    );

  const institutionId =
    instructors?.[0]
      ?.institution_id;

  if (!institutionId) {
    return [];
  }

  return contextRows(
    SUPABASE_URL +
      '/rest/v1/holidays?institution_id=eq.' +
      encodeURIComponent(
        institutionId
      ) +
      '&or=(is_personal.eq.false,instructor_id.eq.' +
      encodeURIComponent(
        instructorId
      ) +
      ')' +
      '&select=*&order=date.asc',

    'Loading calendar holidays'
  );
}


export async function loadCourseDatePickerContext(
  course
) {
  const courseId =
    course?.id;

  if (!courseId) {
    return (
      emptyCourseDatePickerContext()
    );
  }

  const encodedCourseId =
    encodeURIComponent(
      courseId
    );

  const [
    weeks,
    assignments,
    settings,
    scheduleReadings,
    scheduleEvents,
    scheduleItems,
    holidays
  ] =
    await Promise.all([
      contextRows(
        SUPABASE_URL +
          '/rest/v1/course_weeks?course_id=eq.' +
          encodedCourseId +
          '&select=*&order=week_number.asc',

        'Loading course weeks'
      ),

      contextRows(
        SUPABASE_URL +
          '/rest/v1/course_assignments?course_id=eq.' +
          encodedCourseId +
          '&select=*,' +
          'assignment_types(' +
            'name,' +
            'requires_submission,' +
            'assignment_kind' +
          ')' +
          '&order=instance_number.asc',

        'Loading course assignments'
      ),

      contextRows(
        SUPABASE_URL +
          '/rest/v1/course_assignment_type_settings?course_id=eq.' +
          encodedCourseId +
          '&select=*',

        'Loading assignment settings'
      ),

      contextRows(
        SUPABASE_URL +
          '/rest/v1/course_week_reading_assignments?' +
          'select=' +
            'id,' +
            'course_week_id,' +
            'display_order,' +
            'course_readings(id,title,author),' +
            'course_weeks!inner(' +
              'id,' +
              'course_id,' +
              'week_number,' +
              'class_date' +
            ')' +
          '&course_weeks.course_id=eq.' +
          encodedCourseId,

        'Loading scheduled readings'
      ),

      contextRows(
        SUPABASE_URL +
          '/rest/v1/course_events?' +
          'select=' +
            'id,' +
            'course_week_id,' +
            'title,' +
            'event_date,' +
            'event_time,' +
            'location,' +
            'course_weeks!inner(' +
              'id,' +
              'course_id,' +
              'week_number,' +
              'class_date' +
            ')' +
          '&course_weeks.course_id=eq.' +
          encodedCourseId,

        'Loading course events'
      ),

      contextRows(
        SUPABASE_URL +
          '/rest/v1/course_assignment_schedule_items?' +
          'select=*,' +
          'course_assignments!inner(course_id)' +
          '&course_assignments.course_id=eq.' +
          encodedCourseId +
          '&order=display_order.asc,created_at.asc',

        'Loading assignment schedule'
      ),

      loadDatePickerHolidays(
        course?.instructor_id
      )
    ]);

  const typeSettings = {};

  settings.forEach(
    row => {
      const id =
        String(
          row?.assignment_type_id ||
          ''
        );

      if (id) {
        typeSettings[id] =
          row;
      }
    }
  );

  return {
    weeks,
    assignments,
    typeSettings,
    scheduleItems,
    scheduleReadings,
    scheduleEvents,
    holidays
  };
}


const MARKER_META = {
  open: {
    label: 'Opens',
    color: '#2f74b5'
  },

  due: {
    label: 'Due / closes',
    color: '#b7791f'
  },

  holiday: {
    label: 'Holiday',
    color: '#c53030'
  },

  self: {
    label: 'Self-evaluation',
    color: '#805ad5'
  }
};


function dateParts(value) {
  const parts = String(value || '')
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return null;
  }

  return parts;
}


function localDate(value) {
  const parts = dateParts(value);

  if (!parts) return null;

  return new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );
}


function isoDate(date) {
  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, '0'),
    String(
      date.getDate()
    ).padStart(2, '0')
  ].join('-');
}


function prettyDate(value) {
  const date = localDate(value);

  if (!date) {
    return 'No date selected';
  }

  return date.toLocaleDateString(
    undefined,
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }
  );
}


function addDays(value, days) {
  const date = localDate(value);

  if (!date) return null;

  date.setDate(
    date.getDate() +
    Number(days || 0)
  );

  return isoDate(date);
}


function mondayFor(value) {
  const date =
    value instanceof Date
      ? new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate(),
          12,
          0,
          0
        )
      : localDate(value);

  if (!date) return null;

  const day = date.getDay();

  date.setDate(
    date.getDate() -
    (
      day === 0
        ? 6
        : day - 1
    )
  );

  return isoDate(date);
}


function assignmentName(
  assignment,
  assignments,
  typeSettings
) {
  const customName =
    String(
      assignment?.instance_name ||
      ''
    ).trim();

  if (customName) {
    return customName;
  }

  const typeId =
    String(
      assignment
        ?.assignment_type_id ||
      ''
    );

  const baseName =
    String(
      typeSettings?.[typeId]
        ?.display_name ||
      assignment
        ?.assignment_types
        ?.name ||
      'Assignment'
    ).trim() ||
    'Assignment';

  const sameTypeCount =
    (assignments || []).filter(
      candidate =>
        String(
          candidate
            ?.assignment_type_id ||
          ''
        ) === typeId
    ).length;

  if (
    sameTypeCount > 1 &&
    assignment?.instance_number !==
      null &&
    assignment?.instance_number !==
      undefined
  ) {
    return (
      baseName +
      ' ' +
      assignment.instance_number
    );
  }

  return baseName;
}


function scheduleAnchorDate(
  item,
  scheduleReadings,
  scheduleEvents
) {
  if (
    item.item_type ===
    'reading'
  ) {
    const reading =
      (scheduleReadings || [])
        .find(
          candidate =>
            String(
              candidate.id
            ) ===
            String(
              item
                .week_reading_assignment_id ||
              ''
            )
        );

    return (
      reading
        ?.course_weeks
        ?.class_date ||
      null
    );
  }

  const event =
    (scheduleEvents || [])
      .find(
        candidate =>
          String(
            candidate.id
          ) ===
          String(
            item.course_event_id ||
            ''
          )
      );

  return (
    event?.event_date ||
    event
      ?.course_weeks
      ?.class_date ||
    null
  );
}


function buildMarkerMap({
  course,
  assignments,
  typeSettings,
  scheduleItems,
  scheduleReadings,
  scheduleEvents,
  holidays,
  extraMarkers
}) {
  const map = {};

  function add(
    date,
    kind,
    detail
  ) {
    const key =
      String(date || '')
        .slice(0, 10);

    if (!key) return;

    if (!map[key]) {
      map[key] = {};
    }

    if (!map[key][kind]) {
      map[key][kind] = [];
    }

    if (
      detail &&
      !map[key][kind]
        .includes(detail)
    ) {
      map[key][kind].push(
        detail
      );
    }
  }


  (assignments || [])
    .forEach(assignment => {
      const typeId =
        String(
          assignment
            ?.assignment_type_id ||
          ''
        );

      const settings =
        typeSettings?.[typeId] ||
        {};

      const label =
        assignmentName(
          assignment,
          assignments,
          typeSettings
        );

      const scheduleEnabled =
        settings
          .schedule_enabled ===
        true;

      const choiceEnabled =
        settings
          .schedule_choice_enabled ===
        true;


      /*
       * Scheduled assignment.
       *
       * Choice-enabled schedules can vary by student/group,
       * so they deliberately do NOT appear here.
       */
      if (scheduleEnabled) {
        if (choiceEnabled) {
          return;
        }

        const universalItems =
          (scheduleItems || [])
            .filter(
              item =>
                String(
                  item
                    .course_assignment_id ||
                  ''
                ) ===
                  String(
                    assignment.id
                  ) &&
                item
                  .configuration_mode ===
                  'single'
            );

        universalItems
          .forEach(item => {
            const anchor =
              scheduleAnchorDate(
                item,
                scheduleReadings,
                scheduleEvents
              );

            if (!anchor) {
              return;
            }

            if (
              settings
                .schedule_open_offset_days !==
                null &&
              settings
                .schedule_open_offset_days !==
                undefined
            ) {
              add(
                addDays(
                  anchor,
                  settings
                    .schedule_open_offset_days
                ),
                'open',
                label + ' opens'
              );
            }

            if (
              settings
                .schedule_due_offset_days !==
                null &&
              settings
                .schedule_due_offset_days !==
                undefined
            ) {
              add(
                addDays(
                  anchor,
                  settings
                    .schedule_due_offset_days
                ),
                'due',
                label + ' due'
              );
            }
          });

        return;
      }


      /*
       * Ordinary course-wide dates.
       */
      if (assignment.open_date) {
        add(
          assignment.open_date,
          'open',
          label + ' opens'
        );
      }

      if (assignment.due_date) {
        add(
          assignment.due_date,
          'due',
          label + ' due'
        );
      }

      if (
        assignment.close_date &&
        assignment.close_date !==
          assignment.due_date
      ) {
        add(
          assignment.close_date,
          'due',
          label + ' closes'
        );
      }

      if (
        assignment
          .assignment_types
          ?.requires_submission ===
          false &&
        assignment.occurs_on
      ) {
        add(
          assignment.occurs_on,
          'due',
          label + ' occurs'
        );
      }
    });


  /*
   * Holidays.
   */
  (holidays || [])
    .forEach(holiday => {
      if (!holiday?.date) {
        return;
      }

      add(
        holiday.date,
        'holiday',
        holiday.reason
          ? 'Holiday: ' +
            holiday.reason
          : 'Holiday'
      );
    });


  (extraMarkers || [])
    .forEach(marker => {
      if (
        !marker?.date ||
        !MARKER_META[
          marker.kind
        ]
      ) {
        return;
      }

      add(
        marker.date,
        marker.kind,
        marker.detail || ''
      );
    });


  /*
   * Self-evaluation windows.
   */
  if (
    course
      ?.self_eval_enabled ===
    true
  ) {
    [
      [
        course
          .mid_eval_open_date,
        'Mid-Semester Self-Evaluation opens'
      ],
      [
        course
          .mid_eval_close_date,
        'Mid-Semester Self-Evaluation closes'
      ],
      [
        course
          .final_eval_open_date,
        'Final Self-Evaluation opens'
      ],
      [
        course
          .final_eval_close_date,
        'Final Self-Evaluation closes'
      ]
    ].forEach(
      ([date, detail]) => {
        if (date) {
          add(
            date,
            'self',
            detail
          );
        }
      }
    );
  }

  return map;
}


function closestCourseDate(weeks) {
  const values =
    (weeks || [])
      .map(
        week =>
          localDate(
            week.class_date
          )
      )
      .filter(Boolean);

  if (!values.length) {
    return new Date();
  }

  const now = new Date();

  values.sort(
    (a, b) =>
      Math.abs(
        a.getTime() -
        now.getTime()
      ) -
      Math.abs(
        b.getTime() -
        now.getTime()
      )
  );

  return values[0];
}


function monthStartFor(
  value,
  weeks
) {
  const selected =
    localDate(value);

  const base =
    selected ||
    closestCourseDate(weeks);

  return new Date(
    base.getFullYear(),
    base.getMonth(),
    1,
    12,
    0,
    0
  );
}


// DATE_PICKER_OPEN_MONTH_FROM_START_V1

export default function CourseDatePicker({
  value,
  onChange,
  course,
  weeks,
  assignments,
  typeSettings,
  scheduleItems,
  scheduleReadings,
  scheduleEvents,
  holidays,
  extraMarkers = [],
  initialMonthDate = '',
  disabled = false,
  emptyLabel = 'Select date'
}) {
  const [open, setOpen] =
    useState(false);

  const [
    pendingDate,
    setPendingDate
  ] = useState(
    value || ''
  );

  const [
    detailsDate,
    setDetailsDate
  ] = useState(
    value ||
    isoDate(
      closestCourseDate(
        weeks
      )
    )
  );

  const [month, setMonth] =
    useState(
      () =>
        monthStartFor(
          value,
          weeks
        )
    );

  const [
    compact,
    setCompact
  ] = useState(
    () =>
      typeof window !==
        'undefined' &&
      window.innerWidth < 760
  );


  /*
   * Responsive layout.
   */
  useEffect(() => {
    function handleResize() {
      setCompact(
        window.innerWidth < 760
      );
    }

    window.addEventListener(
      'resize',
      handleResize
    );

    return () =>
      window
        .removeEventListener(
          'resize',
          handleResize
        );
  }, []);


  /*
   * Escape closes the dialog.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        'Escape'
      ) {
        setOpen(false);
      }
    }

    document
      .addEventListener(
        'keydown',
        handleKeyDown
      );

    return () =>
      document
        .removeEventListener(
          'keydown',
          handleKeyDown
        );
  }, [open]);


  /*
   * Map each real calendar week to the course week number.
   */
  const weekLabels =
    useMemo(() => {
      const result = {};

      (weeks || [])
        .forEach(week => {
          if (
            !week?.class_date ||
            week.week_number ===
              null ||
            week.week_number ===
              undefined
          ) {
            return;
          }

          const monday =
            mondayFor(
              week.class_date
            );

          if (
            monday &&
            !result[monday]
          ) {
            result[monday] =
              'Week ' +
              week.week_number;
          }
        });

      return result;
    }, [weeks]);


  const markers =
    useMemo(
      () =>
        buildMarkerMap({
          course,
          assignments,
          typeSettings,
          scheduleItems,
          scheduleReadings,
          scheduleEvents,
          holidays,
          extraMarkers
        }),
      [
        course,
        assignments,
        typeSettings,
        scheduleItems,
        scheduleReadings,
        scheduleEvents,
        holidays,
        extraMarkers
      ]
    );


  /*
   * Always construct six full Monday-Sunday rows.
   */
  const gridStart =
    useMemo(() => {
      const first =
        new Date(
          month.getFullYear(),
          month.getMonth(),
          1,
          12,
          0,
          0
        );

      const day =
        first.getDay();

      first.setDate(
        first.getDate() -
        (
          day === 0
            ? 6
            : day - 1
        )
      );

      return first;
    }, [month]);


  const calendarWeeks =
    useMemo(() => {
      const rows = [];

      for (
        let weekIndex = 0;
        weekIndex < 6;
        weekIndex += 1
      ) {
        const days = [];

        for (
          let dayIndex = 0;
          dayIndex < 7;
          dayIndex += 1
        ) {
          days.push(
            new Date(
              gridStart
                .getFullYear(),
              gridStart
                .getMonth(),
              gridStart
                .getDate() +
                weekIndex * 7 +
                dayIndex,
              12,
              0,
              0
            )
          );
        }

        rows.push(days);
      }

      return rows;
    }, [gridStart]);


  function openCalendar() {
    if (disabled) {
      return;
    }

    const initialDate =
      value ||
      initialMonthDate ||
      isoDate(
        closestCourseDate(
          weeks
        )
      );

    setPendingDate(
      value || ''
    );

    setDetailsDate(
      initialDate
    );

    setMonth(
      monthStartFor(
        value ||
        initialMonthDate ||
        initialDate,
        weeks
      )
    );

    setOpen(true);
  }


  function moveMonth(amount) {
    setMonth(
      current =>
        new Date(
          current
            .getFullYear(),
          current
            .getMonth() +
            amount,
          1,
          12,
          0,
          0
        )
    );
  }


  const detailsMarkers =
    markers[
      detailsDate
    ] || {};

  const detailsKinds =
    Object.keys(
      MARKER_META
    ).filter(
      kind =>
        detailsMarkers[
          kind
        ]?.length
    );

  const detailsWeekLabel =
    weekLabels[
      mondayFor(
        detailsDate
      )
    ] || '';

  const monthLabel =
    month
      .toLocaleDateString(
        undefined,
        {
          month: 'long',
          year: 'numeric'
        }
      );


  const modal =
    open &&
    typeof document !==
      'undefined'
      ? createPortal(
          <div
            style={
              styles.overlay
            }
            onMouseDown={
              event => {
                if (
                  event.target ===
                  event.currentTarget
                ) {
                  setOpen(false);
                }
              }
            }
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label=
                "Choose course date"
              style={{
                ...styles.dialog,

                width:
                  compact
                    ? 'min(96vw, 560px)'
                    : 'min(94vw, 920px)'
              }}
            >
              <div
                style={
                  styles.dialogHeader
                }
              >
                <div>
                  <div
                    style={
                      styles.dialogTitle
                    }
                  >
                    Choose date
                  </div>

                  <div
                    style={
                      styles.dialogSubtitle
                    }
                  >
                    {course?.id
                      ? 'Course weeks, deadlines, holidays and self-evaluations'
                      : (holidays || []).length
                        ? 'Existing holidays are marked for reference'
                        : 'Choose a calendar date'}
                  </div>
                </div>

                <button
                  type="button"
                  aria-label=
                    "Close calendar"
                  onClick={() =>
                    setOpen(false)
                  }
                  style={
                    styles.closeButton
                  }
                >
                  ×
                </button>
              </div>


              <div
                style={{
                  ...styles
                    .contentGrid,

                  gridTemplateColumns:
                    compact
                      ? 'minmax(0, 1fr)'
                      : 'minmax(0, 1.45fr) minmax(240px, 0.8fr)'
                }}
              >
                <div
                  style={
                    styles
                      .calendarPanel
                  }
                >
                  <div
                    style={
                      styles.monthHeader
                    }
                  >
                    <button
                      type="button"
                      aria-label=
                        "Previous month"
                      onClick={() =>
                        moveMonth(-1)
                      }
                      style={
                        styles.navButton
                      }
                    >
                      ‹
                    </button>

                    <div
                      style={
                        styles.monthTitle
                      }
                    >
                      {monthLabel}
                    </div>

                    <button
                      type="button"
                      aria-label=
                        "Next month"
                      onClick={() =>
                        moveMonth(1)
                      }
                      style={
                        styles.navButton
                      }
                    >
                      ›
                    </button>
                  </div>


                  <div
                    style={
                      styles.weekdayGrid
                    }
                  >
                    {[
                      'Mon',
                      'Tue',
                      'Wed',
                      'Thu',
                      'Fri',
                      'Sat',
                      'Sun'
                    ].map(
                      label => (
                        <div
                          key={
                            label
                          }
                          style={
                            styles.weekday
                          }
                        >
                          {
                            compact
                              ? label
                                  .slice(
                                    0,
                                    1
                                  )
                              : label
                          }
                        </div>
                      )
                    )}
                  </div>


                  {calendarWeeks.map(
                    days => {
                      const monday =
                        isoDate(
                          days[0]
                        );

                      const rowWeekLabel =
                        weekLabels[
                          monday
                        ] || '';

                      return (
                        <div
                          key={
                            monday
                          }
                          style={
                            styles.weekBlock
                          }
                        >
                          <div
                            style={{
                              ...styles
                                .weekLabel,

                              visibility:
                                rowWeekLabel
                                  ? 'visible'
                                  : 'hidden'
                            }}
                          >
                            {
                              rowWeekLabel ||
                              'Week'
                            }
                          </div>

                          <div
                            style={
                              styles.dayGrid
                            }
                          >
                            {days.map(
                              date => {
                                const key =
                                  isoDate(
                                    date
                                  );

                                const dateMarkers =
                                  markers[
                                    key
                                  ] ||
                                  {};

                                const kinds =
                                  Object.keys(
                                    MARKER_META
                                  ).filter(
                                    kind =>
                                      dateMarkers[
                                        kind
                                      ]
                                        ?.length
                                  );

                                const inMonth =
                                  date
                                    .getMonth() ===
                                  month
                                    .getMonth();

                                const selected =
                                  key ===
                                  pendingDate;

                                const inspected =
                                  key ===
                                  detailsDate;

                                return (
                                  <button
                                    key={
                                      key
                                    }
                                    type=
                                      "button"
                                    onMouseEnter={() =>
                                      setDetailsDate(
                                        key
                                      )
                                    }
                                    onFocus={() =>
                                      setDetailsDate(
                                        key
                                      )
                                    }
                                    onClick={() => {
                                      setPendingDate(
                                        key
                                      );

                                      setDetailsDate(
                                        key
                                      );
                                    }}
                                    style={{
                                      ...styles
                                        .dayButton,

                                      color:
                                        inMonth
                                          ? '#1f2933'
                                          : '#a3adb6',

                                      background:
                                        selected
                                          ? '#eaf3f8'
                                          : inspected
                                            ? '#f7fafc'
                                            : 'white',

                                      border:
                                        selected
                                          ? '2px solid #215b7d'
                                          : inspected
                                            ? '1px solid #9fb3c0'
                                            : '1px solid #e1e7ec'
                                    }}
                                  >
                                    <span
                                      style={
                                        styles
                                          .dayNumber
                                      }
                                    >
                                      {
                                        date
                                          .getDate()
                                      }
                                    </span>

                                    {kinds.length >
                                      0 && (
                                      <span
                                        style={
                                          styles
                                            .markerRow
                                        }
                                      >
                                        {kinds.map(
                                          kind => (
                                            <span
                                              key={
                                                kind
                                              }
                                              aria-label={
                                                MARKER_META[
                                                  kind
                                                ]
                                                  .label
                                              }
                                              style={{
                                                ...styles
                                                  .marker,

                                                background:
                                                  MARKER_META[
                                                    kind
                                                  ]
                                                    .color
                                              }}
                                            />
                                          )
                                        )}
                                      </span>
                                    )}
                                  </button>
                                );
                              }
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}


                  <div
                    style={
                      styles.legend
                    }
                  >
                    {Object.entries(
                      MARKER_META
                    ).map(
                      ([
                        kind,
                        meta
                      ]) => (
                        <span
                          key={
                            kind
                          }
                          style={
                            styles
                              .legendItem
                          }
                        >
                          <span
                            style={{
                              ...styles
                                .legendDot,

                              background:
                                meta.color
                            }}
                          />

                          {
                            meta.label
                          }
                        </span>
                      )
                    )}
                  </div>
                </div>


                <aside
                  style={
                    styles.detailsPanel
                  }
                >
                  <div
                    style={
                      styles.detailsDate
                    }
                  >
                    {
                      prettyDate(
                        detailsDate
                      )
                    }
                  </div>

                  {detailsWeekLabel && (
                    <div
                      style={
                        styles.detailsWeek
                      }
                    >
                      {
                        detailsWeekLabel
                      }
                    </div>
                  )}


                  {detailsKinds.length ===
                  0 ? (
                    <div
                      style={
                        styles.emptyDetails
                      }
                    >
                      No course markers
                      on this date.
                    </div>

                  ) : (
                    <div
                      style={
                        styles.detailsList
                      }
                    >
                      {detailsKinds.map(
                        kind => (
                          <div
                            key={
                              kind
                            }
                            style={
                              styles
                                .detailsGroup
                            }
                          >
                            <div
                              style={
                                styles
                                  .detailsGroupTitle
                              }
                            >
                              <span
                                style={{
                                  ...styles
                                    .detailsDot,

                                  background:
                                    MARKER_META[
                                      kind
                                    ].color
                                }}
                              />

                              {
                                MARKER_META[
                                  kind
                                ].label
                              }
                            </div>

                            {detailsMarkers[
                              kind
                            ].map(
                              detail => (
                                <div
                                  key={
                                    detail
                                  }
                                  style={
                                    styles
                                      .detailItem
                                  }
                                >
                                  {
                                    detail
                                  }
                                </div>
                              )
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}


                  <div
                    style={
                      styles.detailsHint
                    }
                  >
                    Hover over a date
                    to inspect it.
                    Click a date to
                    select it.
                  </div>
                </aside>
              </div>


              <div
                style={
                  styles.footer
                }
              >
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                  style={
                    styles.clearButton
                  }
                >
                  Clear date
                </button>

                <div
                  style={
                    styles.footerRight
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpen(false)
                    }
                    style={
                      styles.cancelButton
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={
                      !pendingDate
                    }
                    onClick={() => {
                      if (
                        !pendingDate
                      ) {
                        return;
                      }

                      onChange(
                        pendingDate
                      );

                      setOpen(
                        false
                      );
                    }}
                    style={{
                      ...styles.useButton,

                      opacity:
                        pendingDate
                          ? 1
                          : 0.45,

                      cursor:
                        pendingDate
                          ? 'pointer'
                          : 'not-allowed'
                    }}
                  >
                    {
                      pendingDate
                        ? 'Use ' +
                          prettyDate(
                            pendingDate
                          )
                        : 'Choose a date'
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>,

          document.body
        )

      : null;


  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={
          openCalendar
        }
        style={{
          ...styles.fieldButton,
          opacity:
            disabled
              ? 0.58
              : 1,
          cursor:
            disabled
              ? 'not-allowed'
              : 'pointer',
          background:
            disabled
              ? '#f2f4f6'
              : 'white'
        }}
      >
        {
          value ||
          emptyLabel
        }
      </button>

      {modal}
    </>
  );
}


const styles = {
  fieldButton: {
    width: '100%',
    minHeight: '38px',
    boxSizing: 'border-box',
    border:
      '1px solid #cbd5df',
    borderRadius: '6px',
    background: 'white',
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: '14px',
    color: '#1f2933',
    cursor: 'pointer'
  },


  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background:
      'rgba(15, 32, 43, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    padding: '16px',
    overflowY: 'auto'
  },


  dialog: {
    maxHeight:
      'calc(100vh - 32px)',
    overflowY: 'auto',
    background: 'white',
    borderRadius: '14px',
    boxShadow:
      '0 22px 70px rgba(16, 42, 67, 0.28)',
    border:
      '1px solid #d7dee5'
  },


  dialogHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems:
      'flex-start',
    justifyContent:
      'space-between',
    gap: '16px',
    padding:
      '16px 18px 14px',
    borderBottom:
      '1px solid #e4e9ed',
    background: 'white'
  },


  dialogTitle: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#1f2933'
  },


  dialogSubtitle: {
    marginTop: '3px',
    fontSize: '12px',
    color: '#6b7c8c'
  },


  closeButton: {
    width: '32px',
    height: '32px',
    border:
      '1px solid #d7dee5',
    borderRadius: '7px',
    background: 'white',
    color: '#526575',
    fontSize: '20px',
    lineHeight: 1,
    cursor: 'pointer'
  },


  contentGrid: {
    display: 'grid',
    gap: '18px',
    padding: '18px'
  },


  calendarPanel: {
    minWidth: 0
  },


  monthHeader: {
    display: 'grid',
    gridTemplateColumns:
      '36px minmax(0, 1fr) 36px',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px'
  },


  monthTitle: {
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '800',
    color: '#1f2933'
  },


  navButton: {
    width: '36px',
    height: '36px',
    border:
      '1px solid #d7dee5',
    borderRadius: '7px',
    background: 'white',
    color: '#215b7d',
    fontSize: '22px',
    lineHeight: 1,
    cursor: 'pointer'
  },


  weekdayGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(7, minmax(0, 1fr))',
    gap: '4px',
    marginBottom: '2px'
  },


  weekday: {
    textAlign: 'center',
    fontSize: '10px',
    fontWeight: '800',
    color: '#7a8894',
    padding: '3px 0'
  },


  weekBlock: {
    marginTop: '4px'
  },


  weekLabel: {
    minHeight: '16px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '2px',
    fontSize: '10px',
    fontWeight: '800',
    color: '#526575',
    letterSpacing:
      '0.02em'
  },


  dayGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(7, minmax(0, 1fr))',
    gap: '4px'
  },


  dayButton: {
    position: 'relative',
    minWidth: 0,
    minHeight: '48px',
    borderRadius: '7px',
    padding:
      '6px 3px 13px',
    cursor: 'pointer',
    fontSize: '12px',
    textAlign: 'center'
  },


  dayNumber: {
    display: 'block'
  },


  markerRow: {
    position: 'absolute',
    left: '3px',
    right: '3px',
    bottom: '5px',
    display: 'flex',
    justifyContent:
      'center',
    gap: '3px'
  },


  marker: {
    width: '6px',
    height: '6px',
    borderRadius: '999px'
  },


  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 14px',
    paddingTop: '12px',
    marginTop: '12px',
    borderTop:
      '1px solid #e5eaee'
  },


  legendItem: {
    display:
      'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '10px',
    color: '#66788a'
  },


  legendDot: {
    width: '7px',
    height: '7px',
    borderRadius: '999px'
  },


  detailsPanel: {
    minWidth: 0,
    alignSelf: 'stretch',
    border:
      '1px solid #e0e6eb',
    borderRadius: '10px',
    background: '#f8fafc',
    padding: '14px'
  },


  detailsDate: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#1f2933'
  },


  detailsWeek: {
    display: 'inline-block',
    marginTop: '6px',
    padding: '3px 7px',
    borderRadius: '999px',
    background: '#eaf1f5',
    color: '#526575',
    fontSize: '10px',
    fontWeight: '800'
  },


  emptyDetails: {
    marginTop: '18px',
    fontSize: '12px',
    color: '#7a8894'
  },


  detailsList: {
    display: 'grid',
    gap: '14px',
    marginTop: '16px'
  },


  detailsGroup: {
    display: 'grid',
    gap: '6px'
  },


  detailsGroupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '11px',
    fontWeight: '800',
    color: '#526575'
  },


  detailsDot: {
    width: '8px',
    height: '8px',
    borderRadius: '999px',
    flex: '0 0 auto'
  },


  detailItem: {
    paddingLeft: '15px',
    fontSize: '12px',
    lineHeight: 1.35,
    color: '#334e5f'
  },


  detailsHint: {
    marginTop: '20px',
    paddingTop: '10px',
    borderTop:
      '1px solid #e2e8ed',
    fontSize: '10px',
    lineHeight: 1.4,
    color: '#8795a1'
  },


  footer: {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '13px 18px',
    borderTop:
      '1px solid #e4e9ed',
    background: 'white'
  },


  footerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },


  clearButton: {
    border: 0,
    background:
      'transparent',
    color: '#66788a',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    padding: '7px 4px'
  },


  cancelButton: {
    border:
      '1px solid #cbd5df',
    borderRadius: '7px',
    background: 'white',
    color: '#526575',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },


  useButton: {
    border:
      '1px solid #215b7d',
    borderRadius: '7px',
    background: '#215b7d',
    color: 'white',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '800'
  }
};
